<template>
  <div class="resource-picker-panel">
    <div class="resource-picker-panel__toolbar">
      <!-- 单行输入；多行粘贴后用绝对定位悬浮抽屉，不顶开下方表格 -->
      <div class="resource-picker-panel__searchBlock">
        <el-input
          v-model="localResourceName"
          size="small"
          clearable
          :readonly="batchNameList.length > 0"
          :placeholder="'资源名称（可粘贴 Excel 多行）'"
          class="resource-picker-panel__searchInput"
          :class="{ 'resource-picker-panel__searchInput--batch': batchNameList.length }"
          @paste.native="handleNamePaste"
          @keyup.enter.native="handleSearch"
          @focus.native="onSearchFocus"
          @clear="clearBatchNames"
        />
        <div
          v-if="batchNameList.length && nameDrawerOpen"
          class="resource-picker-panel__nameDrawer"
          role="region"
          aria-label="已粘贴的资源名称"
        >
          <div class="resource-picker-panel__nameDrawerHead">
            <span class="resource-picker-panel__nameDrawerTitle">
              已粘贴 {{ batchNameList.length }} 个名称（最多 {{ nameDrawerLimit }} 个）
            </span>
            <button
              type="button"
              class="resource-picker-panel__nameDrawerClear"
              @click="clearBatchNames"
            >
              清除
            </button>
          </div>
          <ul class="resource-picker-panel__nameDrawerList">
            <li
              v-for="(name, index) in batchNameList"
              :key="`${index}-${name}`"
              class="resource-picker-panel__nameDrawerItem"
              :title="name"
            >
              <span class="resource-picker-panel__nameDrawerIndex link-font-mono">{{ index + 1 }}</span>
              <span class="resource-picker-panel__nameDrawerText">{{ name }}</span>
            </li>
          </ul>
        </div>
      </div>
      <el-select
        v-if="!lockAssetType"
        v-model="localAssetType"
        size="small"
        clearable
        filterable
        placeholder="类型"
        class="resource-picker-panel__typeSelect"
        popper-class="genericResourceDropdown"
        @change="handleSearch"
      >
        <el-option
          v-for="item in typeOptions"
          :key="item"
          :label="item"
          :value="item"
        />
      </el-select>
      <el-button size="small" type="primary" plain @click="handleSearch">查询</el-button>
      <el-button size="small" @click="handleResetSearch">重置搜索</el-button>
    </div>

    <!-- 主工作区：表格勾选即表示已选，无需单独已选 dock -->
    <div ref="workspace" class="resource-picker-panel__workspace">
      <!--
        查询时只用 v-loading 遮罩，不拆掉 el-table：
        否则固定 height 在卸载/重挂时会塌成滚不动，搜到的行像「闪一下没了」。
      -->
      <div ref="tableWrap" class="resource-picker-panel__tableWrap" v-loading="loading">
            <el-table
            v-if="tableData.length"
            ref="pickerTable"
            class="linkTable linkTable--hybrid linkTable--picker"
            :class="{ 'linkTable--picker--workout': workoutExtaMode }"
            :data="tableData"
            :row-key="getRowKey"
            size="small"
            :height="tableHeight"
            @selection-change="handleSelectionChange"
            @row-click="handleRowClick"
          >
            <el-table-column
              type="selection"
              :width="workoutExtaMode ? workoutPickerWidths.selection : 48"
              align="center"
              :reserve-selection="true"
            />
            <el-table-column
              label="预览"
              :width="workoutExtaMode ? workoutPickerWidths.preview : 68"
              align="center"
              header-align="center"
              class-name="pickerColPreview"
              label-class-name="pickerColPreview"
            >
              <template slot-scope="scope">
                <div class="previewCell">
                  <el-image
                    v-if="scope.row.thumbUrl"
                    class="previewCell__img previewCell__img--picker"
                    :src="scope.row.thumbUrl"
                    :preview-src-list="[scope.row.thumbUrl]"
                    fit="cover"
                  >
                    <div slot="placeholder" class="previewCell__slot">
                      <i class="el-icon-loading"></i>
                    </div>
                    <div slot="error" class="previewCell__slot previewCell__slot--error">
                      <i class="el-icon-picture-outline"></i>
                    </div>
                  </el-image>
                  <div v-else class="previewCell__empty previewCell__empty--picker">
                    <i class="el-icon-picture-outline" />
                  </div>
                </div>
              </template>
            </el-table-column>
            <el-table-column
              label="资源名称"
              prop="resourceName"
              :width="workoutExtaMode ? workoutPickerWidths.resourceName : undefined"
              :min-width="workoutExtaMode ? undefined : 160"
              :align="workoutExtaMode ? 'center' : 'left'"
              :header-align="workoutExtaMode ? 'center' : 'left'"
              :class-name="workoutExtaMode ? 'pickerColCenter pickerColName' : 'pickerColLeft pickerColName'"
              :label-class-name="workoutExtaMode ? 'pickerColCenter' : 'pickerColLeft'"
              show-overflow-tooltip
            />
            <el-table-column
              v-if="!lockAssetType"
              label="类型"
              prop="assetType"
              :width="132"
              align="left"
              header-align="left"
              class-name="pickerColLeft"
              label-class-name="pickerColLeft"
            >
              <template slot-scope="scope">
                <el-tag
                  v-if="scope.row.assetType"
                  size="mini"
                  effect="plain"
                  :class="['typeTag', `typeTag--${getTypeTagVariant(scope.row.assetType)}`]"
                >
                  {{ scope.row.assetType }}
                </el-tag>
                <span v-else class="cellMuted">—</span>
              </template>
            </el-table-column>
            <el-table-column
              label="状态"
              prop="onOrOff"
              :width="workoutExtaMode ? workoutPickerWidths.status : 96"
              :align="workoutExtaMode ? 'center' : 'left'"
              :header-align="workoutExtaMode ? 'center' : 'left'"
              :class-name="workoutExtaMode ? 'pickerColCenter' : 'pickerColLeft'"
              :label-class-name="workoutExtaMode ? 'pickerColCenter' : 'pickerColLeft'"
            >
              <template slot-scope="scope">
                <span
                  class="statusPill"
                  :class="'statusPill--' + getStatusPillVariant(scope.row.onOrOff)"
                >
                  <span class="statusPill__dot" aria-hidden="true" />
                  {{ getStatusLabel(scope.row.onOrOff) }}
                </span>
              </template>
            </el-table-column>
            <el-table-column
              v-for="col in workoutExtaColumns"
              :key="col.key"
              :label="col.label"
              :width="col.width"
              :min-width="col.minWidth"
              :align="col.renderAs === 'tagChips' ? 'left' : 'center'"
              header-align="center"
              :class-name="
                col.renderAs === 'tagChips'
                  ? 'pickerColLeft pickerColExta pickerColTags'
                  : 'pickerColCenter pickerColExta'
              "
              label-class-name="pickerColCenter pickerColExtaHead"
            >
              <template slot-scope="scope">
                <span v-if="scope.row.workoutExtaLoading" class="cellMuted">…</span>
                <span
                  v-else-if="col.renderAs === 'boolPill'"
                  class="statusPill"
                  :class="'statusPill--' + getBoolPillVariant(pickerExtaCell(scope.row, col.key))"
                >
                  <span class="statusPill__dot" aria-hidden="true" />
                  {{ formatBoolPillLabel(pickerExtaCell(scope.row, col.key)) }}
                </span>
                <div
                  v-else-if="col.renderAs === 'tagChips'"
                  class="pickerTagChips"
                >
                  <template v-if="pickerTagTitles(scope.row).length">
                    <span
                      v-for="(title, tagIndex) in pickerTagTitles(scope.row)"
                      :key="`${scope.row.key || scope.row.id}-${tagIndex}`"
                      class="pickerTagChip"
                    >{{ title }}</span>
                  </template>
                  <span v-else class="cellMuted">—</span>
                </div>
                <span
                  v-else
                  class="pickerExtaCell link-font-mono"
                >{{ pickerExtaCell(scope.row, col.key) }}</span>
              </template>
            </el-table-column>
            <!-- 非 workout：最右 gutter 吸收剩余宽；workout 由 tags minWidth 吃剩余 -->
            <el-table-column
              v-if="!workoutExtaMode"
              min-width="1"
              label=""
              class-name="pickerTableColGutter"
              label-class-name="pickerTableColGutter"
            />
          </el-table>

          <el-empty
            v-else-if="!loading"
            description="当前范围内无资源"
            :image-size="72"
          >
            <div class="resource-picker-panel__emptyHint">请调整列表筛选或修改上方搜索条件</div>
          </el-empty>
        </div>

      <div class="resource-picker-panel__pagination">
        <el-pagination
          v-if="!batchNameMode"
          class="linkPagination"
          small
          layout="prev, pager, next, sizes"
          :total="listTotalCount"
          :page-sizes="[30, 50, 100]"
          :page-size="pageSize"
          :current-page.sync="pageNum"
          popper-class="genericResourceDropdown"
          @size-change="handleSizeChange"
          @current-change="handlePageChange"
        />
        <span v-else class="resource-picker-panel__pageMeta">
          多行精确匹配 · 共 {{ listTotalCount }} 条
        </span>
        <span v-if="!batchNameMode && listTotalCount > 0" class="resource-picker-panel__pageMeta">
          第 {{ pageNum }} / {{ totalPages }} 页
        </span>
      </div>
    </div>
  </div>
</template>

<script>
import {
  getGenericResourceList,
  getGenericResourceById,
  getGenericResourceListByExactNames,
} from '../../../../api/genericResource'
import { runWithConcurrency } from '../asyncConcurrency'
import { normalizeGenericResourceList, toLinkListQueryParams } from '../genericResourceAdapter'
import {
  buildBindingRefMeta,
  buildWorkoutPickerExtaMap,
  getWorkoutPickerExtaColumns,
  isWorkoutPickerAssetType,
  WORKOUT_PICKER_COLUMN_WIDTHS,
} from '../form/basicForms/workoutBindingUtils'
import { mergePageSelection, syncTableRowSelection } from '../linkSelectionHelper'
import {
  getTypeTagVariant,
  getStatusLabel,
  getStatusPillVariant,
  formatBoolPillLabel,
  getBoolPillVariant,
} from './resourceDisplayHelper'
import {
  BATCH_PASTE_NAME_LIMIT,
  parsePastedResourceNames,
} from './resourcePickerNameBatch'

export default {
  name: 'ResourcePickerPanel',
  props: {
    value: { type: Array, default: () => [] },
    filters: { type: Object, default: () => ({}) },
    filterSummary: { type: String, default: '无' },
    hasFilters: { type: Boolean, default: false },
    /** 与主列表一致的资源类型选项，供弹窗内按类型筛选 */
    typeOptions: { type: Array, default: () => [] },
    /** 锁定资源类型：隐藏类型下拉，查询固定为该类型（合集绑定挑选等场景） */
    lockAssetType: { type: String, default: '' },
    /** workout 合集挑选：列表不含 exta，需补拉详情并展示 difficulty / loopable / gender 等 */
    workoutExtaMode: { type: Boolean, default: false },
  },
  data() {
    return {
      tableData: [],
      listTotalCount: 0,
      loading: false,
      pageNum: 1,
      pageSize: 30,
      localResourceName: '',
      localAssetType: '',
      appliedResourceName: '',
      appliedAssetType: '',
      isSyncingTableSelection: false,
      tableHeight: 320,
      tableResizeObserver: null,
      tableHeightRafId: 0,
      /** 多行粘贴查询时关闭常规分页，一次展示全部命中 */
      batchNameMode: false,
      /** Excel 多行粘贴后的名称列表（最多 nameDrawerLimit 个）；悬浮抽屉展示 */
      batchNameList: [],
      nameDrawerLimit: BATCH_PASTE_NAME_LIMIT,
      nameDrawerOpen: false,
      /** 列表请求代数：慢请求后到时若已过期则丢弃，避免盖掉新结果 */
      listFetchSeq: 0,
    }
  },
  computed: {
    selection: {
      get() {
        return this.value || []
      },
      set(next) {
        this.$emit('input', next)
        this.$emit('change', next)
      },
    },
    totalPages() {
      if (this.listTotalCount <= 0) {
        return 1
      }
      return Math.max(1, Math.ceil(this.listTotalCount / this.pageSize))
    },
    workoutExtaColumns() {
      if (!this.workoutExtaMode) {
        return []
      }
      return getWorkoutPickerExtaColumns(this.lockAssetType)
    },
    workoutPickerWidths() {
      return WORKOUT_PICKER_COLUMN_WIDTHS
    },
  },
  watch: {
    filters: {
      deep: true,
      handler() {
        this.fetchList()
      },
    },
    /**
     * loading 结束后再 sync 勾选，并重测表格高度，避免查询后 body 滚不动。
     */
    loading(value) {
      if (!value) {
        this.$nextTick(() => {
          this.syncTableSelectionFromStore()
          this.remeasureTableHeight()
        })
      }
    },
    selection: {
      deep: true,
      handler() {
        if (this.loading) {
          return
        }
        this.$nextTick(() => {
          this.syncTableSelectionFromStore()
        })
      },
    },
    /** 列表总数变化时同步到弹窗说明显示屏（范围轮播首帧） */
    listTotalCount() {
      this.emitScopeMeta()
    },
  },
  mounted() {
    if (this.lockAssetType) {
      this.localAssetType = this.lockAssetType
      this.appliedAssetType = this.lockAssetType
    }
    this.fetchList()
    this.$nextTick(() => {
      this.setupTableHeightObserver()
    })
  },
  beforeDestroy() {
    if (this.tableResizeObserver) {
      this.tableResizeObserver.disconnect()
      this.tableResizeObserver = null
    }
    if (this.tableHeightRafId) {
      cancelAnimationFrame(this.tableHeightRafId)
      this.tableHeightRafId = 0
    }
  },
  methods: {
    /**
     * 将可选范围摘要上报给弹窗顶栏轮播，与列表控制条「全部资源」帧同位展示。
     */
    emitScopeMeta() {
      this.$emit('scope-meta-change', {
        totalCount: this.listTotalCount,
      })
    },
    setupTableHeightObserver() {
      const wrap = this.$refs.tableWrap
      if (!wrap) {
        return
      }
      const applyHeight = () => {
        const next = Math.floor(wrap.clientHeight)
        if (next > 0 && next !== this.tableHeight) {
          this.tableHeight = next
          this.$nextTick(() => {
            const table = this.$refs.pickerTable
            if (table && typeof table.doLayout === 'function') {
              table.doLayout()
            }
          })
        }
      }
      const scheduleApply = () => {
        if (this.tableHeightRafId) {
          cancelAnimationFrame(this.tableHeightRafId)
        }
        this.tableHeightRafId = requestAnimationFrame(() => {
          this.tableHeightRafId = 0
          applyHeight()
        })
      }
      scheduleApply()
      if (typeof ResizeObserver === 'undefined') {
        return
      }
      if (this.tableResizeObserver) {
        this.tableResizeObserver.disconnect()
      }
      this.tableResizeObserver = new ResizeObserver(scheduleApply)
      this.tableResizeObserver.observe(wrap)
    },
    getTypeTagVariant,
    getStatusLabel,
    getStatusPillVariant,
    formatBoolPillLabel,
    getBoolPillVariant,
    shouldEnrichWorkoutExta() {
      return this.workoutExtaMode && isWorkoutPickerAssetType(this.lockAssetType)
    },
    pickerExtaCell(row, key) {
      const map = row.workoutExtaMap
      if (!map || typeof map !== 'object') {
        return '—'
      }
      return map[key] != null && map[key] !== '' ? map[key] : '—'
    },
    /** 挑选表 tags 列：用 chip 展示，允许换行看全 */
    pickerTagTitles(row) {
      const map = row && row.workoutExtaMap
      if (!map || typeof map !== 'object') {
        return []
      }
      if (Array.isArray(map.tagTitles) && map.tagTitles.length) {
        return map.tagTitles.filter(Boolean)
      }
      const joined = map.tags
      if (typeof joined === 'string' && joined && joined !== '—') {
        return joined.split(/\s*·\s*/).filter(Boolean)
      }
      return []
    },
    async enrichWorkoutExtaSummaries() {
      const assetType = this.lockAssetType
      if (!assetType || !this.tableData.length) {
        return
      }
      await runWithConcurrency(this.tableData, async (row) => {
          if (!row.id) {
            this.$set(row, 'workoutExtaLoading', false)
            this.$set(row, 'workoutExtaMap', {})
            return
          }
          try {
            const res = await getGenericResourceById({ id: row.id })
            if (res.success && res.data) {
              const meta = buildBindingRefMeta(assetType, res.data)
              this.$set(
                row,
                'workoutExtaMap',
                buildWorkoutPickerExtaMap(assetType, meta)
              )
            } else {
              this.$set(row, 'workoutExtaMap', {})
            }
          } catch (e) {
            this.$set(row, 'workoutExtaMap', {})
          } finally {
            this.$set(row, 'workoutExtaLoading', false)
          }
        })
      // tags 等补全后行高变了，强制 doLayout，否则 body 可视高度偶发卡死
      this.$nextTick(() => {
        const table = this.$refs.pickerTable
        if (table && typeof table.doLayout === 'function') {
          table.doLayout()
        }
        this.remeasureTableHeight()
      })
    },
    getRowKey(row) {
      return row.key
    },
    buildQueryParams(resourceNameOverride) {
      const inherited = this.filters || {}
      const resourceName =
        resourceNameOverride != null
          ? resourceNameOverride
          : this.appliedResourceName !== ''
            ? this.appliedResourceName
            : inherited.resourceName || ''
      const assetType = this.lockAssetType
        ? this.lockAssetType
        : this.appliedAssetType !== ''
          ? this.appliedAssetType
          : inherited.assetType || ''
      return toLinkListQueryParams({
        pageNum: this.pageNum,
        pageSize: this.pageSize,
        resourceName,
        assetType,
        wallpaperStatus: inherited.wallpaperStatus,
        categoryId: inherited.categoryType,
        key: inherited.key || '',
        dataSetId: inherited.dataSetId,
      })
    },
    decoratePickerRows(rows) {
      return normalizeGenericResourceList(rows).map((row) => ({
        ...row,
        workoutExtaMap: null,
        workoutExtaLoading: this.shouldEnrichWorkoutExta(),
      }))
    },
    applyFetchedRows(rows, totalCount) {
      this.listTotalCount = totalCount
      this.tableData = this.decoratePickerRows(rows)
      this.$nextTick(() => {
        this.syncTableSelectionFromStore()
        this.remeasureTableHeight()
      })
      if (this.shouldEnrichWorkoutExta()) {
        this.enrichWorkoutExtaSummaries()
      }
    },
    fetchList() {
      if (this.batchNameList.length >= 2) {
        this.fetchListByExactNames(this.batchNameList)
        return
      }
      if (this.batchNameList.length === 1) {
        this.appliedResourceName = this.batchNameList[0]
      }
      const parsed = parsePastedResourceNames(this.appliedResourceName)
      const names = parsed.names
      this.batchNameMode = false
      const seq = (this.listFetchSeq += 1)
      this.loading = true
      const singleName = names.length === 1 ? names[0] : this.appliedResourceName
      getGenericResourceList(this.buildQueryParams(singleName || undefined))
        .then((res) => {
          if (seq !== this.listFetchSeq) {
            return
          }
          if (!res.success) {
            this.tableData = []
            this.listTotalCount = 0
            return
          }
          const parsedTotal = Number(res.data?.total)
          this.applyFetchedRows(
            res.data?.wallpaperVOList || [],
            Number.isFinite(parsedTotal) ? parsedTotal : 0
          )
        })
        .catch(() => {
          if (seq !== this.listFetchSeq) {
            return
          }
          this.$message.error('加载资源列表失败')
          this.tableData = []
          this.listTotalCount = 0
        })
        .finally(() => {
          if (seq === this.listFetchSeq) {
            this.loading = false
          }
        })
    },
    /**
     * Excel 多行粘贴：一次 POST 精确名批量查询（服务端大小写不敏感 + 按输入序）。
     * 未命中名用 message 提示；用 listFetchSeq 丢弃过期响应。
     */
    async fetchListByExactNames(names) {
      this.batchNameMode = true
      const seq = (this.listFetchSeq += 1)
      this.loading = true
      this.pageNum = 1
      try {
        const inherited = this.filters || {}
        const assetType = this.lockAssetType
          ? this.lockAssetType
          : this.appliedAssetType !== ''
            ? this.appliedAssetType
            : inherited.assetType || ''
        const res = await getGenericResourceListByExactNames({
          names,
          wallpaperType: assetType || undefined,
          wallpaperStatus:
            inherited.wallpaperStatus === '' || inherited.wallpaperStatus == null
              ? undefined
              : Number(inherited.wallpaperStatus),
        })
        if (seq !== this.listFetchSeq) {
          return
        }
        if (!res.success) {
          this.$message.error(res.errMessage || '批量按名称查询失败')
          this.tableData = []
          this.listTotalCount = 0
          return
        }
        const rows = normalizeGenericResourceList(res.data?.wallpaperVOList)
        const missingNames = Array.isArray(res.data?.missingNames) ? res.data.missingNames : []
        this.applyFetchedRows(rows, rows.length)
        if (missingNames.length) {
          const preview = missingNames.slice(0, 5).join('、')
          const more = missingNames.length > 5 ? ` 等 ${missingNames.length} 个` : ''
          this.$message.warning(`未找到：${preview}${more}`)
        } else {
          this.$message.success(`已精确匹配 ${rows.length} 个资源`)
        }
      } catch (e) {
        if (seq !== this.listFetchSeq) {
          return
        }
        this.$message.error('批量按名称查询失败')
        this.tableData = []
        this.listTotalCount = 0
      } finally {
        if (seq === this.listFetchSeq) {
          this.loading = false
        }
      }
    },
    syncTableSelectionFromStore() {
      const table = this.$refs.pickerTable
      if (!table || !this.tableData.length) {
        return
      }
      this.isSyncingTableSelection = true
      syncTableRowSelection(table, this.tableData, this.selection)
      this.$nextTick(() => {
        this.isSyncingTableSelection = false
      })
    },
    handleSelectionChange(pageSelected) {
      if (this.isSyncingTableSelection) {
        return
      }
      this.selection = mergePageSelection(this.selection, this.tableData, pageSelected)
    },
    handleRowClick(row, _column, event) {
      if (!event || !event.target) {
        return
      }
      if (event.target.closest('a, button, .el-button, .el-checkbox, .el-tag, img, .el-image')) {
        return
      }
      const table = this.$refs.pickerTable
      if (table) {
        table.toggleRowSelection(row)
      }
    },
    /**
     * Excel 多行粘贴：截获后展开悬浮名称抽屉（最多 30 个，大小写不敏感去重）；
     * 单行仍走默认粘贴进输入框。
     */
    handleNamePaste(event) {
      const clipboard = event && event.clipboardData
      const text = clipboard ? clipboard.getData('text') : ''
      const { names, truncated, totalUnique } = parsePastedResourceNames(text)
      if (names.length < 2) {
        return
      }
      event.preventDefault()
      this.batchNameList = names
      this.localResourceName = `已粘贴 ${names.length} 个名称`
      this.nameDrawerOpen = true
      if (truncated) {
        this.$message.warning(
          `一次最多 ${BATCH_PASTE_NAME_LIMIT} 个名称，已保留前 ${names.length} 个（共识别 ${totalUnique} 个）`
        )
      }
    },
    onSearchFocus() {
      if (this.batchNameList.length) {
        this.nameDrawerOpen = true
      }
    },
    clearBatchNames() {
      this.batchNameList = []
      this.batchNameMode = false
      this.nameDrawerOpen = false
      this.localResourceName = ''
      this.appliedResourceName = ''
    },
    handleSearch() {
      this.appliedAssetType = this.localAssetType || ''
      if (!this.batchNameList.length) {
        this.appliedResourceName = (this.localResourceName || '').trim()
      }
      this.nameDrawerOpen = false
      this.pageNum = 1
      this.fetchList()
    },
    handleResetSearch() {
      this.localResourceName = ''
      this.localAssetType = ''
      this.appliedResourceName = ''
      this.appliedAssetType = ''
      this.batchNameList = []
      this.batchNameMode = false
      this.nameDrawerOpen = false
      this.pageNum = 1
      this.fetchList()
    },
    handleSizeChange(size) {
      this.pageSize = size
      this.pageNum = 1
      this.fetchList()
    },
    handlePageChange(page) {
      this.pageNum = page
      this.fetchList()
    },
    remeasureTableHeight() {
      this.$nextTick(() => {
        this.setupTableHeightObserver()
        setTimeout(() => {
          this.setupTableHeightObserver()
        }, 120)
      })
    },
  },
}
</script>

<style scoped>
@import '../linkTableHybrid.css';

.resource-picker-panel {
  display: flex;
  flex-direction: column;
  gap: 6px;
  height: 100%;
  min-height: 0;
  flex: 1 1 auto;
  overflow: hidden;
}

.resource-picker-panel__toolbar {
  flex-shrink: 0;
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
}

.resource-picker-panel__searchBlock {
  position: relative;
  z-index: 6;
  flex: 0 0 auto;
  width: 280px;
}

.resource-picker-panel__searchInput {
  width: 280px;
}

.resource-picker-panel__searchInput--batch >>> .el-input__inner {
  color: var(--link-ink-secondary, #57534e);
  cursor: pointer;
}

/* 悬浮抽屉：绝对定位盖在表格上方，不撑开工具栏/下方内容 */
.resource-picker-panel__nameDrawer {
  position: absolute;
  top: calc(100% + 4px);
  left: 0;
  width: 320px;
  max-width: min(360px, 70vw);
  z-index: 30;
  border: 1px solid var(--link-line-strong, #e5dfd6);
  border-radius: 8px;
  background: #fff;
  box-shadow: 0 8px 24px rgba(41, 37, 36, 0.12);
  overflow: hidden;
}

.resource-picker-panel__nameDrawerHead {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  padding: 6px 8px;
  border-bottom: 1px solid var(--link-line-strong, #e5dfd6);
  background: var(--link-surface-muted, #f7f4ef);
}

.resource-picker-panel__nameDrawerTitle {
  font-size: var(--link-text-xs, 12px);
  color: var(--link-ink-secondary, #57534e);
  line-height: 1.3;
}

.resource-picker-panel__nameDrawerClear {
  border: 0;
  padding: 0;
  background: transparent;
  color: var(--link-accent, #a67c52);
  font-size: var(--link-text-xs, 12px);
  cursor: pointer;
}

.resource-picker-panel__nameDrawerClear:hover {
  color: var(--link-ink, #292524);
}

.resource-picker-panel__nameDrawerList {
  list-style: none;
  margin: 0;
  padding: 4px 0;
  max-height: 220px;
  overflow-y: auto;
}

.resource-picker-panel__nameDrawerItem {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 4px 8px;
  min-width: 0;
}

.resource-picker-panel__nameDrawerIndex {
  flex: 0 0 18px;
  color: var(--link-ink-muted, #a8a29e);
  font-size: 11px;
  text-align: right;
}

.resource-picker-panel__nameDrawerText {
  flex: 1 1 auto;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-size: var(--link-text-sm, 13px);
  color: var(--link-ink, #292524);
}

.resource-picker-panel__typeSelect {
  width: 160px;
}

.resource-picker-panel__workspace {
  flex: 1 1 auto;
  min-height: 0;
  display: flex;
  flex-direction: column;
  gap: 6px;
  overflow: hidden;
}

.resource-picker-panel__tableWrap {
  flex: 1 1 auto;
  min-height: 0;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  border-radius: 8px;
  border: 1px solid var(--link-line-strong, #e5dfd6);
  background: var(--link-surface-row, #faf8f5);
}

.resource-picker-panel__tableWrap >>> .el-table {
  background: transparent;
  width: 100%;
}

.linkTable--picker >>> .el-table__body tr {
  cursor: pointer;
}

.cellMuted {
  color: var(--link-ink-muted);
  font-size: var(--link-text-sm);
}

.resource-picker-panel__loading {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  height: 100%;
  color: var(--link-ink-muted);
  font-size: var(--link-text-md);
}

.resource-picker-panel__emptyHint {
  font-size: var(--link-text-sm);
  color: var(--link-ink-muted);
}

.resource-picker-panel__pagination {
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  min-height: 40px;
  padding: 2px 0 6px;
  overflow: visible;
}

.resource-picker-panel__pageMeta {
  font-size: var(--link-text-sm);
  color: var(--link-ink-tertiary);
  white-space: nowrap;
}
</style>
